import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import SearchIcon from '@atlaskit/icon/core/search';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline } from '@atlaskit/primitives/compiled';

import { CommandIcon } from '../../../../../common/constants/icons/command';
import { ReturnIcon } from '../../../../../common/constants/icons/return';
import { ISSUE_NAVIGATOR_URL } from '../../../../../common/constants/issue-navigator';
import { PrimaryProductKeys } from '../../../../../common/constants/products';
import { useAppContext } from '../../../../../common/ui/app-context';
import { SearchFooter } from '../../../../../common/ui/quick-find/search-dialog-footer';
import { isJqlQueryDetected } from '../../../../../common/utils/intent-detection';
import { isOnJiraFullPageSearch } from '../../../../../common/utils/is-on-jira-full-page-search';
import { getWorkItemPageUrl } from '../../../../../common/utils/quick-find/jira-work-item-navigation';
import type { State } from '../../../../../common/utils/quick-find/state';
import { useSelectedFilters } from '../../../../../controllers/filters/selected';
import { type QuickFindFooterEvent } from '../../../../../controllers/quick-find/utils';
import { usePrimaryProduct } from '../../../../../controllers/store/bootstrap';
import { useQuickFindQuery } from '../../../../../controllers/store/quick-find';

import { messages } from './messages';

const ACTION_SUBJECT_ID = 'jiraAdvancedIssueSearch';

type Props = {
	state: State;
	onClick: (props: QuickFindFooterEvent) => void;
};

export const JiraAdvancedSearchLink = ({ state, onClick }: Props): React.JSX.Element => {
	const intl = useIntl();
	const { generateSearchUrl, queryParams } = useAppContext();
	const [primaryProduct] = usePrimaryProduct();
	const quickFindQuery = useQuickFindQuery();
	const {
		selectedFilters: { jiraFilters },
	} = useSelectedFilters({});

	const swapQuickFindCTAs =
		isOnJiraFullPageSearch() && primaryProduct && primaryProduct === PrimaryProductKeys.Jira;

	const icon = (
		<SearchIcon
			label={fg('ai-a11y-111-19638-35211') ? '' : intl.formatMessage(messages.jiraSearchWorkItems)}
			color="currentColor"
		/>
	);

	const rightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{swapQuickFindCTAs && <CommandIcon />}
			<ReturnIcon />
		</Inline>
	);

	const workItemPageUrl = useMemo(() => {
		try {
			return getWorkItemPageUrl({
				query: quickFindQuery,
				jiraFilters,
				generateSearchUrl,
				queryParams,
				isQueryJql: isJqlQueryDetected(quickFindQuery),
			});
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (error) {
			return ISSUE_NAVIGATOR_URL;
		}
	}, [queryParams, generateSearchUrl, quickFindQuery, jiraFilters]);

	const onResultClicked = useCallback(
		(event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>) => {
			onClick({ state, event, actionSubjectId: ACTION_SUBJECT_ID });
		},
		[onClick, state],
	);

	return (
		<SearchFooter
			icon={icon}
			title={intl.formatMessage(messages.jiraSearchWorkItems)}
			rightMetadata={rightMetadata}
			url={workItemPageUrl}
			onResultClicked={onResultClicked}
		/>
	);
};
